// Contract-handshake telemetry helpers. Thin wrappers over the client log ring
// buffer (logRingBuffer) that record the preprocess round-trip, the user's
// contract edits, and the server-allocated contract key as DISTINCT ui.* events —
// so a bug-report bundle reconstructs the full contract lifecycle timeline.
//
// All functions are best-effort: a telemetry failure must never throw into the
// forecast/preprocess flow. PII discipline: record lengths/markers, never full
// question text or resolution criteria (mirrors ui.forecast.submit, which
// truncates the question).
import { recordUiEvent, setUiContext } from "./logRingBuffer";
import { withIntent } from "./telemetry/instrument";

// The allocated contract key (User#<hash>) arrived over the forecast SSE stream.
// Pin it as ambient context (so every later event in the bundle carries it) AND
// emit one discrete assignment event.
export function recordContractKey({ key, key_status } = {}) {
  try {
    setUiContext({ contract_key: key ?? null, contract_key_status: key_status ?? null });
    recordUiEvent({
      event: "ui.forecast.contract_key",
      key: key ?? null,
      key_status: key_status ?? null,
    });
  } catch {
    /* telemetry is best-effort — never break the forecast flow */
  }
}

// The raw question was sent to /preprocess. Record the length only (not the text).
export function recordPreprocessRequest(questionText) {
  try {
    recordUiEvent({ event: "ui.preprocess.request", question_len: (questionText || "").length });
  } catch {
    /* best-effort */
  }
}

// The refined contract came back from /preprocess. Record the resolution date +
// whether criteria were produced — markers, not the criteria text.
export function recordPreprocessResult(preprocessed) {
  try {
    const pp = preprocessed || {};
    recordUiEvent({
      event: "ui.preprocess.result",
      resolution_date: pp.scheduled_resolution_time || null,
      has_criteria: !!pp.resolution_criteria,
    });
  } catch {
    /* best-effort */
  }
}

// The user edited the preprocessed contract before submitting. `field` names which
// contract field changed; extra marker fields (lengths, changed flags) may be
// passed but never the edited text itself.
/**
 * @param {{field?: string|null, forecastId?: string|null, [marker: string]: unknown}} [edit]
 */
export function recordContractEdit({ field, forecastId = null, ...markers } = {}) {
  try {
    recordUiEvent({ event: "ui.contract.edit", field: field ?? null, ...markers });
    // An edit that changed the contract is the user's `contract.edit` (P2 design §4.6): the
    // field, never its text. The run it starts counts under its own intent.
    if (markers.changed !== false) {
      withIntent(
        "contract.edit",
        () => undefined,
        { forecast_id: forecastId, field: field ?? null },
        { surface: "preprocess" },
      );
    }
  } catch {
    /* best-effort */
  }
}
